import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { IToolPanelParams } from 'ag-grid-community';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/auth/auth.service';
import { MessageService } from 'primeng/api';
import { ItemSetService } from 'src/app/cms-v2/entities/item-set/item-set-service/item-set.service';
import { DialogService } from 'primeng/dynamicdialog';
import { ItemSetSelectorDialogComponent } from './item-set-selector-dialog/item-set-selector-dialog.component';
import { NewSetDialogComponent } from './new-set-dialog/new-set-dialog.component';

@Component({
  selector: 'app-item-sets-tool-panel',
  templateUrl: './item-sets-tool-panel.component.html',
  styleUrls: ['./item-sets-tool-panel.component.sass'],
  providers: [DialogService]
})
export class ItemSetsToolPanelComponent implements OnInit {
  @ViewChild('searchContainer', { static: false }) searchContainer!: ElementRef;
  
  private params!: IToolPanelParams;
  public gui!: HTMLElement;

  enabledSets: any[] = [];
  filteredSets: any[] = [];
  selectedItemSet: any = null;
  selectedItems: any[] = [];
  currentUser: any;

  loading = false;
  error: string | null = null;
  
  // Filtering and sorting properties
  searchTerm: string = '';
  sortBy: 'alphabetical' | 'size' = 'alphabetical';
  sortDirection: 'asc' | 'desc' = 'asc';
  
  // UI state
  showAddToSetDropdown = false;
  
  // Resize functionality
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;

  constructor(
    private http: HttpClient,
    private router: Router,
    private authService: AuthService,
    private itemSetService: ItemSetService,
    private messageService: MessageService,
    private dialogService: DialogService
  ) {
    this.gui = document.createElement('div');
  }

  async ngOnInit() {
    const result = await this.authService.getSocialUser();
  
    this.currentUser = result?.currentUser || result;
    
    await this.loadItemSets();
  }

  agInit(params: IToolPanelParams): void {
    this.params = params;
  }

  getGui(): HTMLElement {
    return this.gui;
  }

  refresh(): void {
    this.loadItemSets();
  }

  async loadItemSets() {
    try {
      this.loading = true;
      this.error = null;
  
      // Use optimized endpoint with retry logic
      const response = await this.retryOptimizedItemSets(3, 1000);
    
      this.enabledSets = response.filter((set: any) => set.enabled);
      
      // Initialize UI state for each set
      this.enabledSets.forEach(set => {
        set.items_ref = []; // Will be loaded on-demand
        set.thumbnailsLoaded = false;
        set.thumbnailsExpanded = false;
        set.loadingThumbnails = false;
        set.previewThumbnails = [];
        set.currentPage = 0; // For carousel pagination
        set.thumbnailsPerPage = 5; // Show 5 images at a time
        // itemCount comes from backend aggregate pipeline
      });
      
      this.applyFiltersAndSort();
      
    } catch (err) {
      this.error = 'Failed to load item sets. Please try again.';
      console.error('Error loading item sets:', err);
    } finally {
      this.loading = false;
    }
  }

  /**
   * Retry optimized item sets endpoint with exponential backoff
   * @param maxRetries Maximum number of retry attempts
   * @param initialDelay Initial delay in milliseconds (doubles with each retry)
   */
  private async retryOptimizedItemSets(maxRetries: number = 3, initialDelay: number = 1000): Promise<any[]> {
    let lastError: any;
    
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        console.log(`[ItemSets] Attempt ${attempt + 1}/${maxRetries + 1} to load optimized item sets`);
        const response = await this.itemSetService.getOptimizedItemSets();
        console.log(`[ItemSets] Successfully loaded ${response.length} item sets`);
        return response;
      } catch (error) {
        lastError = error;
        console.error(`[ItemSets] Attempt ${attempt + 1} failed:`, error);
        
        // If this wasn't the last attempt, wait before retrying
        if (attempt < maxRetries) {
          const delay = initialDelay * Math.pow(2, attempt); // Exponential backoff
          console.log(`[ItemSets] Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    // All retries failed
    console.error(`[ItemSets] All ${maxRetries + 1} attempts failed. Last error:`, lastError);
    throw new Error(`Failed to load item sets after ${maxRetries + 1} attempts: ${lastError?.message || 'Unknown error'}`);
  }

  async toggleThumbnails(set: any) {
    if (!set.thumbnailsLoaded && !set.loadingThumbnails) {
      // Load thumbnails on-demand (this will set thumbnailsExpanded = true)
      await this.loadThumbnails(set);
      // Don't toggle after initial load, loadThumbnails already sets it to true
    } else {
      // Only toggle if thumbnails are already loaded
      set.thumbnailsExpanded = !set.thumbnailsExpanded;
    }
  }

  async loadThumbnails(set: any) {
    try {
      set.loadingThumbnails = true;
      
      const setWithThumbnails = await this.itemSetService.loadItemSetThumbnails(set.id);
      
      set.items_ref = setWithThumbnails.items_ref || [];
      set.thumbnailsLoaded = true; // ← Move this BEFORE calling getSetPreviewThumbnails
      set.previewThumbnails = this.getSetPreviewThumbnails(set);
      set.thumbnailsExpanded = true;
      
    } catch (error) {
      set.items_ref = [];
      set.previewThumbnails = [];
      set.thumbnailsLoaded = false;
    } finally {
      set.loadingThumbnails = false;
    }
  }

  // Background loading methods removed - now using on-demand lazy loading



  async selectItemSet(itemSet: any) {
    // Load full details for viewing (this is different from thumbnail loading)
    try {
      this.loading = true;
      const setDetails = await this.itemSetService.findItemSetWithItems(itemSet.id);
      itemSet.items_ref = setDetails.items_ref || [];
      itemSet.itemCount = itemSet.items_ref.length;
      
      this.selectedItemSet = itemSet;
      this.selectedItems = [];
    } catch (error) {
      console.error('Error loading set details:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Load Failed',
        detail: 'Failed to load item set details'
      });
      return;
    } finally {
      this.loading = false;
    }
  }

  backToList() {
    this.selectedItemSet = null;
    this.selectedItems = [];
  }

  removeItem(item: any) {
    const index = this.selectedItemSet.items_ref.findIndex((i: any) => i.id === item.id);
    if (index !== -1) {
      this.selectedItemSet.items_ref.splice(index, 1);
      this.updateItemSet();
    }
  }

  async moveSelectedItems() {
    if (!this.selectedItems || this.selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select items from the table first'
      });
      return;
    }
  
    const selectedSetId = await this.openItemSetSelector(true); // Use all sets for move operation
    console.log('Selected Set ID:', selectedSetId);
    console.log('Selected Items:', this.selectedItems);
  
    if (!selectedSetId) {
      console.warn('No set selected');
      return;
    }
  
    try {
      const payload = this.selectedItems.map(item => ({ _id: item._id }));
      console.log('Payload to move:', payload);
  
      await this.itemSetService.moveToItemSet(selectedSetId, payload, this.selectedItemSet.id);
  
      this.selectedItemSet.items_ref = this.selectedItemSet.items_ref.filter(
        (item: any) => !this.selectedItems.some(moved => moved._id === item._id)
      );
  
      await this.loadItemSets();
  
      this.selectedItems = [];
  
      this.messageService.add({
        severity: 'success',
        summary: 'Items Moved',
        detail: `Moved ${payload.length} item(s) successfully`
      });
  
    } catch (error: any) {
      console.error('Error moving items:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Move Failed',
        detail: error?.message || 'Unable to move items'
      });
    }
  }

  async copySelectedItems() {
    if (!this.selectedItems || this.selectedItems.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select items from the table first'
      });
      return;
    }
  
    const selectedSetId = await this.openItemSetSelector(true); // Use all sets for copy operation
    console.log('Selected Set ID for copy:', selectedSetId);
    console.log('Selected Items to copy:', this.selectedItems);
  
    if (!selectedSetId) {
      console.warn('No set selected for copy');
      return;
    }
  
    try {
      const payload = this.selectedItems.map(item => ({ _id: item._id }));
  
      await this.itemSetService.copyToItemSet(selectedSetId, payload);
  
      this.messageService.add({
        severity: 'success',
        summary: 'Items Copied',
        detail: `Copied ${payload.length} item(s) successfully`
      });
  
      await this.loadItemSets();
  
    } catch (err: any) {
      console.error('Error copying items:', err);
      this.messageService.add({
        severity: 'error',
        summary: 'Failed to Copy Items',
        detail: err?.message || 'Unable to copy items'
      });
    }
  }

  private openItemSetSelector(useAllSets: boolean = false): Promise<number | undefined> {
    return new Promise((resolve) => {
      const ref = this.dialogService.open(ItemSetSelectorDialogComponent, {
        header: 'Select Item Set',
        width: '600px',
        height: '500px',
        data: {
          itemSets: useAllSets ? this.enabledSets : this.filteredSets,
          currentSetId: this.selectedItemSet?.id
        }
      });
  
      ref.onClose.subscribe((selectedId: number | undefined) => {
        console.log('[Dialog] Set selected:', selectedId); 
        resolve(selectedId);
      });
    });
  }
  

  deleteSelectedItems() {
    if (this.selectedItems.length > 0) {
      this.selectedItems.forEach(item => this.removeItem(item));
      this.selectedItems = [];
    }
  }

  async addSelectedFromMain() {
    if (!this.selectedItemSet) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Item Set Selected',
        detail: 'Please select an item set first'
      });
      return;
    }

    const selectedFromMain = this.params.api.getSelectedRows();
    if (!selectedFromMain || selectedFromMain.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select items from the main table first'
      });
      return;
    }

    if (!this.selectedItemSet.items_ref) {
      this.selectedItemSet.items_ref = [];
    }

    const newItems = selectedFromMain.filter(newItem => 
      !this.selectedItemSet.items_ref.some((existingItem: any) => existingItem.id === newItem.id)
    );

    if (newItems.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: 'No New Items',
        detail: 'All selected items are already in the set'
      });
      return;
    }

    this.selectedItemSet.items_ref.push(...newItems);
    await this.updateItemSet();

    // Clear selected rows from main grid
    this.params.api.deselectAll();

    this.messageService.add({
      severity: 'success',
      summary: 'Items Added',
      detail: `Added ${newItems.length} items to the set`
    });
  }

  private async updateItemSet() {
    try {
      if (!this.selectedItemSet) {
        throw new Error('No item set selected');
      }

      const updateDto = {
        items_ref: this.selectedItemSet.items_ref.map((item: any) => ({
          _id: item._id,
          id: item.id
        }))
      };

      await this.itemSetService.updateItemSet(this.selectedItemSet.id, updateDto);
      await this.loadItemSets(); // Reload the list after successful update
      
      this.messageService.add({
        severity: 'success',
        summary: 'Set Updated',
        detail: 'Item set updated successfully'
      });
    } catch (error: any) {
      console.error('Error updating item set:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: error.error?.message || 'Failed to update item set. Please try again.'
      });
    }
  }

  // Filtering and sorting methods
  applyFiltersAndSort() {
    let filtered = [...this.enabledSets];
    
    // Apply search filter
    if (this.searchTerm.trim()) {
      const searchLower = this.searchTerm.toLowerCase();
      filtered = filtered.filter(set => 
        set.name?.toLowerCase().includes(searchLower) ||
        set.internalName?.toLowerCase().includes(searchLower) ||
        set.id?.toString().includes(this.searchTerm.trim())
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      if (this.sortBy === 'alphabetical') {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        comparison = nameA.localeCompare(nameB);
      } else if (this.sortBy === 'size') {
        comparison = (a.itemCount || 0) - (b.itemCount || 0);
      }
      
      return this.sortDirection === 'desc' ? -comparison : comparison;
    });
    
    this.filteredSets = filtered;
  }
  
  onSearchChange() {
    this.applyFiltersAndSort();
  }
  
  onSortChange(sortBy: 'alphabetical' | 'size') {
    if (this.sortBy === sortBy) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortBy = sortBy;
      this.sortDirection = 'asc';
    }
    this.applyFiltersAndSort();
  }
  
  // New Set functionality
  async createNewSet() {
    const selectedFromMain = this.params.api.getSelectedRows();
    
    const ref = this.dialogService.open(NewSetDialogComponent, {
      header: 'Create New Item Set',
      width: '500px',
      height: 'auto',
      data: {
        selectedItems: selectedFromMain || []
      }
    });

    ref.onClose.subscribe(async (newSet: any) => {
      if (newSet) {
        console.log('New set created:', newSet);
        
        // Clear selected items from main grid
        this.params.api.deselectAll();
        
        // Reload the sets list
        await this.loadItemSets();
      }
    });
  }
  
  // Add to Existing Set functionality
  async addToExistingSet() {
    const selectedFromMain = this.params.api.getSelectedRows();
    if (!selectedFromMain || selectedFromMain.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Items Selected',
        detail: 'Please select items from the main table first'
      });
      return;
    }

    const selectedSetId = await this.openItemSetSelector();
    if (!selectedSetId) {
      return;
    }

    try {
      const payload = selectedFromMain.map(item => ({ _id: item._id }));
      await this.itemSetService.addToItemSet({
        itemSetsId: selectedSetId,
        sourcingItems: payload,
        existingItem: true
      });

      this.params.api.deselectAll();
      await this.loadItemSets();

      this.messageService.add({
        severity: 'success',
        summary: 'Items Added',
        detail: `Added ${payload.length} items to the set successfully`
      });

    } catch (error: any) {
      console.error('Error adding items to set:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Failed to Add Items',
        detail: error?.message || 'Unable to add items to set'
      });
    }
  }
  
  // Get all thumbnails for a set (no limit for carousel)
  getSetPreviewThumbnails(set: any): any[] {
    if (!set.items_ref || !Array.isArray(set.items_ref) || !set.thumbnailsLoaded) {
      return [];
    }
    
    const itemsWithThumbnails = set.items_ref.filter((item: any) => {
      return item.thumbnail_ref?.thumbnails?.some((thumb: any) => thumb.path);
    });
    
    return itemsWithThumbnails; // Return all thumbnails for carousel
  }

  // Get visible thumbnails for initial display (first 5)
  getVisibleThumbnails(set: any): any[] {
    const allThumbnails = this.getSetPreviewThumbnails(set);
    return allThumbnails.slice(0, 5);
  }

  // Get the best thumbnail URL for an item (preferably 256px, fallback to any available)
  getThumbnailUrl(item: any): string {
    if (!item?.thumbnail_ref?.thumbnails) {
      return '';
    }
    
    // Try to find 256px thumbnail first (best size for display)
    const preferred = item.thumbnail_ref.thumbnails.find((thumb: any) => thumb.size === 256);
    if (preferred?.path) {
      return preferred.path;
    }
    
    // Fallback to any available thumbnail
    const available = item.thumbnail_ref.thumbnails.find((thumb: any) => thumb.path);
    return available?.path || '';
  }
  
  // Check if set has any items at all (using itemCount from backend)
  hasAnyItems(set: any): boolean {
    return set.itemCount > 0;
  }
  
  // Check if set is completely empty (using itemCount from backend)
  isSetEmpty(set: any): boolean {
    return set.itemCount === 0;
  }
  
  // Check if thumbnails can be loaded
  canLoadThumbnails(set: any): boolean {
    return set.itemCount > 0 && !set.thumbnailsLoaded && !set.loadingThumbnails;
  }
  
  // Check if thumbnails are currently loading
  isLoadingThumbnails(set: any): boolean {
    return set.loadingThumbnails;
  }
  
  // Check if thumbnails are loaded and expanded
  hasThumbnailsExpanded(set: any): boolean {
    return set.thumbnailsLoaded && set.thumbnailsExpanded;
  }

  // Check if we should show the expandable content
  shouldShowExpandedContent(set: any): boolean {
    return this.isLoadingThumbnails(set) || 
           this.hasThumbnailsExpanded(set) || 
           (this.isSetEmpty(set) && set.thumbnailsExpanded);
  }

  // Get tooltip text based on set state
  getTooltipText(set: any): string {
    if (this.isLoadingThumbnails(set)) {
      return 'Loading images...';
    }
    if (set.thumbnailsExpanded) {
      return 'Click to hide images';
    }
    return `Click to show ${set.itemCount} images`;
  }
  
  // Check if set has items but no thumbnails (after loading)
  hasItemsButNoThumbnails(set: any): boolean {
    return set.thumbnailsLoaded && 
           set.items_ref && 
           set.items_ref.length > 0 && 
           this.getSetPreviewThumbnails(set).length === 0;
  }

  // Get items without thumbnails for display
  getItemsWithoutThumbnails(set: any): any[] {
    if (!set.thumbnailsLoaded || !set.items_ref || !Array.isArray(set.items_ref)) {
      return [];
    }
    
    return set.items_ref.filter((item: any) => {
      return !item.thumbnail_ref?.thumbnails?.some((thumb: any) => thumb.path);
    });
  }

  // Check if there are items without thumbnails to show
  hasItemsWithoutThumbnails(set: any): boolean {
    return this.getItemsWithoutThumbnails(set).length > 0;
  }

  // Get visible items without thumbnails (first 5)
  getVisibleItemsWithoutThumbnails(set: any): any[] {
    const itemsWithoutThumbnails = this.getItemsWithoutThumbnails(set);
    return itemsWithoutThumbnails.slice(0, 5);
  }

  // Check if there are more items without thumbnails than visible (for scroll indicator)
  hasMoreItemsWithoutThumbnails(set: any): boolean {
    return this.getItemsWithoutThumbnails(set).length > 5;
  }

  // Carousel navigation methods - Page-based navigation
  goToPreviousPage(set: any) {
    if (set.currentPage > 0) {
      set.currentPage--;
    }
  }

  goToNextPage(set: any) {
    const totalPages = this.getTotalPages(set);
    if (set.currentPage < totalPages - 1) {
      set.currentPage++;
    }
  }

  // Get total number of pages for carousel
  getTotalPages(set: any): number {
    const allThumbnails = this.getSetPreviewThumbnails(set);
    return Math.ceil(allThumbnails.length / set.thumbnailsPerPage);
  }

  // Get current page thumbnails (exactly 5)
  getCurrentPageThumbnails(set: any): any[] {
    const allThumbnails = this.getSetPreviewThumbnails(set);
    const startIndex = set.currentPage * set.thumbnailsPerPage;
    const endIndex = startIndex + set.thumbnailsPerPage;
    return allThumbnails.slice(startIndex, endIndex);
  }

  // Check if can go to previous page
  canGoPrevious(set: any): boolean {
    return set.currentPage > 0;
  }

  // Check if can go to next page
  canGoNext(set: any): boolean {
    const totalPages = this.getTotalPages(set);
    return set.currentPage < totalPages - 1;
  }

  // Get page info for display
  getPageInfo(set: any): string {
    const totalPages = this.getTotalPages(set);
    if (totalPages <= 1) return '';
    return `${set.currentPage + 1} / ${totalPages}`;
  }
  
  // Handle image load events for better UX
  onImageLoad(event: Event) {
    const img = event.target as HTMLImageElement;
    img.classList.remove('loading');
    img.style.opacity = '1'; // Ensure image is visible when loaded
  }
  
  // Get the visible thumbnail count for display (always 5 max visible)
  getVisibleThumbnailCount(set: any): number {
    const thumbnails = this.getSetPreviewThumbnails(set);
    return Math.min(thumbnails.length, 5);
  }
  
  // Check if there are more thumbnails than visible (for scroll indicator)
  hasMoreThumbnails(set: any): boolean {
    return this.getSetPreviewThumbnails(set).length > 5;
  }
  
  onImageError(event: Event) {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none'; // Hide broken images
    console.warn('Failed to load image:', img.src);
  }

  // Navigation methods
  navigateToItem(item: any) {
    if (item && item.id) {
      const url = `/items/${item.id}`;
      window.open(url, '_blank');
    }
  }

  navigateToItemFromThumbnail(item: any, event: Event) {
    event.stopPropagation(); // Prevent triggering the thumbnail toggle
    this.navigateToItem(item);
  }
  
  // Check if set details are still loading
  isSetDetailsLoading(set: any): boolean {
    return !set.detailsLoaded;
  }

  // Resize functionality
  startResize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    
    this.isResizing = true;
    this.startX = event.clientX;
    this.startWidth = this.searchContainer.nativeElement.offsetWidth;
    
    // Add global event listeners
    document.addEventListener('mousemove', this.onResize.bind(this));
    document.addEventListener('mouseup', this.stopResize.bind(this));
    
    // Prevent text selection during resize
    document.body.style.userSelect = 'none';
    document.body.style.cursor = 'ew-resize';
  }

  private onResize(event: MouseEvent): void {
    if (!this.isResizing) return;
    
    event.preventDefault();
    
    const deltaX = event.clientX - this.startX;
    const newWidth = this.startWidth + deltaX;
    
    // Apply min/max constraints
    const minWidth = 200;
    const maxWidth = 500;
    const constrainedWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));
    
    // Apply the new width
    this.searchContainer.nativeElement.style.width = `${constrainedWidth}px`;
  }

  private stopResize(): void {
    if (!this.isResizing) return;
    
    this.isResizing = false;
    
    // Remove global event listeners
    document.removeEventListener('mousemove', this.onResize.bind(this));
    document.removeEventListener('mouseup', this.stopResize.bind(this));
    
    // Restore normal cursor and text selection
    document.body.style.userSelect = '';
    document.body.style.cursor = '';
  }

}

